import Environment from './environment';

export type QualityReleaseFormData = {
  customs_dispatch_number?: string;
  reception_date?: string | null;
  product_description_pm?: string;
  provider?: string;
  model?: string;
  lot_serial?: string;
  quantity?: number;
  expiration_date?: string | null;
  packing_list_check?: string;
  origin_label?: string;
  packaging_condition?: string;
  quantities_correct?: string;
  reception_observations?: string;
  quarantine_reason?: string;
  supplier_rectification?: string;
  supplier_rectification_date?: string | null;
  non_conformity_started?: string;
  non_conformity_number?: string;
  non_conformity_date?: string | null;
  visual_control?: string;
  importer_label?: string;
  additional_test_required?: string;
  expiry_in_time?: string;
  final_inspection_observations?: string;
  labeling_importer_name?: string;
  labeling_legibility?: string;
  labeling_product_name?: string;
  labeling_dt_data?: string;
  labeling_pm_number?: string;
  labeling_observations?: string;
  qc_signature?: string;
  release_signature?: string;
  qc_sign_date?: string | null;
  release_sign_date?: string | null;
};

export type FunctionalityVerificationFormData = {
  customs_dispatch_number?: string;
  verification_date?: string | null;
  product_description_pm?: string;
  provider?: string;
  model?: string;
  lot_serial?: string;
  quantity?: number;
  expiration_date?: string | null;
  purchase_order_notes?: string;
  components_yes?: string;
  components_no?: string;
  components_observations?: string;
  accessories_yes?: string;
  accessories_no?: string;
  accessories_observations?: string;
  software_license_yes?: string;
  software_license_no?: string;
  software_license_observations?: string;
  software_version_yes?: string;
  software_version_no?: string;
  software_version_observations?: string;
  plugs_yes?: string;
  plugs_no?: string;
  plugs_observations?: string;
  cables_yes?: string;
  cables_no?: string;
  cables_observations?: string;
  fuses_yes?: string;
  fuses_no?: string;
  fuses_observations?: string;
  other_observations?: string;
  signature_line?: string;
};

export type InventoryInspectionForms = {
  quality_release?: QualityReleaseFormData;
  functionality_verification?: FunctionalityVerificationFormData;
};

export const yesNoOptions = [
  { value: 'SI', label: 'Yes (SI)' },
  { value: 'NO', label: 'No (NO)' },
];

export const okOptions = [
  { value: 'OK', label: 'OK' },
  { value: 'NO OK', label: 'Not OK' },
];

export const emptyInspectionForms = (): InventoryInspectionForms => ({
  quality_release: {},
  functionality_verification: {},
});

export function formatLotSerial(batchNumber: string, serialNumber: string) {
  const batch = batchNumber.trim();
  const serial = serialNumber.trim();
  if (batch && serial) {
    return `${batch} / SN${serial}`;
  }
  if (batch) {
    return batch;
  }
  if (serial) {
    return `SN${serial}`;
  }
  return '';
}

function formatFormDate(value: Date | null): string | null {
  if (!value) {
    return null;
  }
  return value.toISOString().split('T')[0];
}

/** Values copied from the main inventory form / product for PDF export only. */
export function buildDefaultInspectionForms(input: {
  batch_number: string;
  serial_number: string;
  provider: string;
  manufacturer_invoice: string;
  expiration_date: Date | null;
  visual_validation_date: Date | null;
  functionality_verification_date: Date | null;
  product_name: string;
  regulatory_class?: string | null;
  model_number?: string | null;
}): InventoryInspectionForms {
  const productDescription = [input.regulatory_class, input.product_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  const lotSerial = formatLotSerial(input.batch_number, input.serial_number);
  const expiration = formatFormDate(input.expiration_date);
  const visualValidationDate = formatFormDate(input.visual_validation_date);
  const functionalityVerificationDate = formatFormDate(
    input.functionality_verification_date,
  );

  return {
    quality_release: {
      customs_dispatch_number: input.manufacturer_invoice || '',
      reception_date: visualValidationDate,
      product_description_pm: productDescription,
      provider: input.provider || '',
      model: input.model_number || '',
      lot_serial: lotSerial,
      quantity: 1,
      expiration_date: expiration,
      qc_sign_date: visualValidationDate,
      release_sign_date: visualValidationDate,
    },
    functionality_verification: {
      customs_dispatch_number: input.manufacturer_invoice || '',
      verification_date: functionalityVerificationDate,
      product_description_pm: productDescription,
      provider: input.provider || '',
      model: input.model_number || '',
      lot_serial: lotSerial,
      quantity: 1,
      expiration_date: expiration,
    },
  };
}

export async function downloadInventoryDocument(
  inventoryId: string,
  documentType: 'quality-release' | 'functionality-verification',
  accessToken: string,
) {
  const path =
    documentType === 'quality-release'
      ? 'quality-release'
      : 'functionality-verification';
  const response = await fetch(
    `${Environment.VITE_API_URL}/api/v1/inventory/${inventoryId}/documents/${path}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const detail = typeof body.detail === 'string' ? body.detail : null;
    throw new Error(detail ?? 'Could not generate document.');
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match?.[1] ?? `inventory-${documentType}.xlsx`;
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}
